import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useOptionsEntitlements } from '../context/OptionsEntitlementsContext';
import {
  fetchOptionsAccount,
  fetchOptionsCapitalPeriods,
  fetchOptionsCurrentCapitalPeriods,
  fetchOptionsEquityHistory,
  fetchOptionsPerformance,
  fetchOptionsPerformanceCapitalSummary,
  syncOptionsAccount,
} from '../lib/optionsApi';
import { buildCustomRangePayload, getDefaultCustomRange } from '../components/options/optionsDashboardUtils';
import { useOptionsMode } from '../context/OptionsModeContext';
import { useOptionsLocale } from '../context/OptionsLanguageContext';
import { emitOptionsOpenOperationsUpdated } from '../lib/optionsEvents';

export const useOptionsPerformanceData = (connected, reloadOverview) => {
  const { mode } = useOptionsMode();
  const { t } = useOptionsLocale();
  const [accountData, setAccountData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [capitalPeriodsData, setCapitalPeriodsData] = useState(null);
  const [capitalPeriodsAllData, setCapitalPeriodsAllData] = useState(null);
  const [currentCapitalPeriodsData, setCurrentCapitalPeriodsData] = useState(null);
  // `loading` gates the FULL-PAGE loader (mount / demo<->real switch only).
  const [loading, setLoading] = useState(true);
  // `rangeFetching` is LOCAL to the 30D/90D/YTD/ALL/Custom selector. It never gates
  // the full page - Performance stays mounted and previous data stays visible.
  const [rangeFetching, setRangeFetching] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [selectedRange, setSelectedRange] = useState('30d');
  const { isPro, loading: entitlementsLoading } = useOptionsEntitlements();

  const [customRange, setCustomRange] = useState(getDefaultCustomRange());
  const [appliedCustomRange, setAppliedCustomRange] = useState(getDefaultCustomRange());
  const rangeRequestIdRef = useRef(0);
  const initialPayloadRef = useRef(null);

  const requestPayload = useMemo(() => {
    if (selectedRange !== 'custom') {
      return { range: selectedRange, start: null, end: null };
    }

    const { start, end } = buildCustomRangePayload(appliedCustomRange);
    return { range: selectedRange, start, end };
  }, [selectedRange, appliedCustomRange]);

  const loadCurrentAccount = useCallback(async () => {
    const [account, currentCapitalPeriods, allCapitalPeriods] = await Promise.all([
      fetchOptionsAccount(),
      isPro ? fetchOptionsCurrentCapitalPeriods() : Promise.resolve(null),
      isPro ? fetchOptionsCapitalPeriods({ range: 'all' }) : fetchOptionsPerformanceCapitalSummary(),
    ]);
    setAccountData(account);
    setCurrentCapitalPeriodsData(currentCapitalPeriods);
    setCapitalPeriodsAllData(allCapitalPeriods);
    return account;
  }, [isPro]);

  // Reads already-synced/persisted performance data for a given range. Never
  // triggers a Deribit sync - only queries /performance, /equity-history and
  // /capital-periods for the requested range/start/end.
  const loadRangeBoundData = useCallback(async (payload) => {
    const [performance, history, capitalPeriods] = await Promise.all([
      fetchOptionsPerformance(payload),
      isPro ? fetchOptionsEquityHistory({ ...payload, limit: 500 }) : Promise.resolve(null),
      isPro ? fetchOptionsCapitalPeriods(payload) : Promise.resolve(null),
    ]);
    setPerformanceData(performance);
    setHistoryData(history);
    setCapitalPeriodsData(capitalPeriods);
  }, [isPro]);

  const loadAll = useCallback(async (payload) => {
    if (!connected) {
      setLoading(false);
      setAccountData(null);
      setPerformanceData(null);
      setHistoryData(null);
      setCapitalPeriodsData(null);
      setCapitalPeriodsAllData(null);
      setCurrentCapitalPeriodsData(null);
      return;
    }

    setLoading(true);
    try {
      await Promise.all([loadCurrentAccount(), loadRangeBoundData(payload)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.failedLoadPerformance'));
    } finally {
      setLoading(false);
    }
  }, [connected, loadCurrentAccount, loadRangeBoundData, t]);

  // Mount / connected / demo<->real switch ONLY. Intentionally does not depend on
  // selectedRange so changing the period never remounts/reloads the whole page.
  useEffect(() => {
    if (entitlementsLoading) return;
    initialPayloadRef.current = requestPayload;
    loadAll(requestPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, mode, entitlementsLoading, isPro]);

  // Range/custom-range change ONLY. Local loading state, keeps previous data
  // visible, never touches the full-page `loading` state or triggers a sync.
  useEffect(() => {
    if (entitlementsLoading || !connected || requestPayload === initialPayloadRef.current) {
      return;
    }
    const requestId = rangeRequestIdRef.current + 1;
    rangeRequestIdRef.current = requestId;
    setRangeFetching(true);
    loadRangeBoundData(requestPayload)
      .catch((error) => {
        toast.error(error.response?.data?.detail || t('toasts.failedLoadPerformance'));
      })
      .finally(() => {
        if (rangeRequestIdRef.current === requestId) {
          setRangeFetching(false);
        }
      });
  }, [connected, entitlementsLoading, requestPayload, loadRangeBoundData, t]);

  const handleApplyCustomRange = useCallback(() => {
    if (!isPro) return;
    if (!customRange.startDate || !customRange.endDate) {
      toast.error(t('toasts.selectBothDates'));
      return;
    }
    if (customRange.startDate > customRange.endDate) {
      toast.error(t('toasts.startBeforeEnd'));
      return;
    }

    setSelectedRange('custom');
    setAppliedCustomRange(customRange);
  }, [customRange, isPro, t]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await syncOptionsAccount();
      toast.success(t('toasts.syncCompleted'));
      await Promise.all([reloadOverview?.({ silent: true }), loadCurrentAccount(), loadRangeBoundData(requestPayload)]);
      emitOptionsOpenOperationsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.syncFailed'));
    } finally {
      setSyncing(false);
    }
  }, [loadCurrentAccount, loadRangeBoundData, requestPayload, reloadOverview, t]);

  return {
    accountData,
    performanceData,
    historyData,
    capitalPeriodsData,
    capitalPeriodsAllData,
    currentCapitalPeriodsData,
    loading,
    rangeFetching,
    syncing,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    handleApplyCustomRange,
    handleSync,
  };
};

export default useOptionsPerformanceData;